import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import "./Pricing.css";
import { ENDPOINTS } from "../api/api";
import useFetch from "../hooks/useFetch";

/* ── Main premium tier card ── */
const TierCard = ({ tier, index }) => (
  <article
    className={`sv-tier ${tier.featured ? "sv-tier--featured" : ""}`}
    style={{ animationDelay: `${index * 0.08}s` }}
  >
    {tier.featured && (
      <span className="sv-tier__ribbon">
        <i className="fa-solid fa-star"></i>
        Most Popular
      </span>
    )}

    <span className="sv-tier__tag">{tier.tag}</span>

    <div className="sv-tier__icon">
      <i className={tier.icon}></i>
    </div>

    <h3 className="sv-tier__name">{tier.name}</h3>
    <p className="sv-tier__desc">{tier.desc}</p>

    <div className="sv-tier__price-block">
      <div className="sv-tier__from">Starting from</div>
      <div className="sv-tier__price">
        <span className="sv-tier__currency">KSh</span>
        <span className="sv-tier__amount">{tier.price.toLocaleString()}</span>
        <span className="sv-tier__period">{tier.period}</span>
      </div>
      <div className="sv-tier__timeline">
        <i className="fa-regular fa-clock"></i>
        {tier.timeline}
      </div>
    </div>

    <ul className="sv-tier__features">
      {(tier.features || []).map((f) => (
        <li key={f.t} className={f.on ? "" : "is-muted"}>
          <i className={f.on ? "fa-solid fa-check" : "fa-solid fa-xmark"}></i>
          <span>{f.t}</span>
        </li>
      ))}
    </ul>

    <NavLink to="/contact" className="sv-tier__cta">
      {tier.cta || "Get Started"}
      <i className="fa-solid fa-arrow-right"></i>
    </NavLink>
  </article>
);

/* ── Compact card for secondary packages ── */
const CompactCard = ({ tier, index }) => (
  <article className="sv-compact" style={{ animationDelay: `${index * 0.06}s` }}>
    <div className="sv-compact__head">
      <div className="sv-compact__icon">
        <i className={tier.icon}></i>
      </div>
      <span className="sv-compact__tag">{tier.tag}</span>
    </div>
    <h3 className="sv-compact__name">{tier.name}</h3>
    <p className="sv-compact__desc">{tier.desc}</p>
    <div className="sv-compact__price">
      <span className="sv-compact__currency">KSh</span>
      <span className="sv-compact__amount">{tier.price.toLocaleString()}</span>
      <span className="sv-compact__period">{tier.period}</span>
    </div>
    <div className="sv-compact__timeline">
      <i className="fa-regular fa-clock"></i> {tier.timeline}
    </div>
    <ul className="sv-compact__features">
      {(tier.features || []).filter((f) => f.on).slice(0, 5).map((f) => (
        <li key={f.t}><i className="fa-solid fa-check"></i><span>{f.t}</span></li>
      ))}
    </ul>
    <NavLink to="/contact" className="sv-compact__cta">
      {tier.cta || "Get Started"}
      <i className="fa-solid fa-arrow-right"></i>
    </NavLink>
  </article>
);

/* ── Add-on card ── */
const AddonCard = ({ tier, index }) => (
  <article className="sv-addon" style={{ animationDelay: `${index * 0.05}s` }}>
    <div className="sv-addon__icon">
      <i className={tier.icon}></i>
    </div>
    <div className="sv-addon__body">
      <h4 className="sv-addon__name">{tier.name}</h4>
      <p className="sv-addon__desc">{tier.desc}</p>
    </div>
    <div className="sv-addon__right">
      <div className="sv-addon__price">
        <span className="sv-addon__currency">KSh</span>
        <span className="sv-addon__amount">{tier.price.toLocaleString()}</span>
      </div>
      <span className="sv-addon__period">{tier.period}</span>
      <NavLink to="/contact" className="sv-addon__cta">
        Add <i className="fa-solid fa-plus"></i>
      </NavLink>
    </div>
  </article>
);

const Pricing = () => {
  const [billing, setBilling] = useState("fixed");

  const { data: tiers,      loading: loadingTiers }      = useFetch(`${ENDPOINTS.pricing}?billing_type=${billing}`);
  const { data: guarantees, loading: loadingGuarantees } = useFetch(ENDPOINTS.pricingGuarantees);

  const TIERS      = tiers      || [];
  const GUARANTEES = guarantees || [];

  if (loadingTiers || loadingGuarantees) {
    return (
      <section className="sv-pricing">
        <div className="sv-pricing-inner">
          <div style={{ textAlign: "center", padding: "4rem 0", opacity: 0.5 }}>
            <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "2rem" }}></i>
          </div>
        </div>
      </section>
    );
  }

  /* ── Split plans by tag ── */
  const isAddon = (t) => (t.tag || "").toUpperCase().includes("ADD");
  const addons  = TIERS.filter(isAddon);
  const packages = TIERS.filter((t) => !isAddon(t));

  /* main 3 = featured one + first two, or just first 3 */
  const featuredIdx = packages.findIndex((t) => t.featured);
  let mainTiers, morePackages;

  if (packages.length <= 3) {
    mainTiers = packages;
    morePackages = [];
  } else {
    // Take 3 main tiers — ensure featured is among them
    mainTiers = packages.slice(0, 3);
    if (featuredIdx >= 3) {
      mainTiers = [packages[0], packages[1], packages[featuredIdx]];
    }
    const mainIds = new Set(mainTiers.map((t) => t.id));
    morePackages = packages.filter((t) => !mainIds.has(t.id));
  }

  return (
    <section className="sv-pricing">
      <div className="sv-pricing-bg" aria-hidden="true">
        <div className="sv-pricing-orb sv-pricing-orb--1"></div>
        <div className="sv-pricing-orb sv-pricing-orb--2"></div>
        <div className="sv-pricing-grid"></div>
      </div>

      <div className="sv-pricing-inner">
        <header className="sv-pricing-header">
          <span className="sv-pricing-eyebrow">
            <i className="fa-solid fa-tag"></i>
            Investment &amp; Plans
          </span>
          <h2 className="sv-pricing-title">
            Transparent pricing,
            <span className="sv-pricing-title-accent"> built for African scale</span>
          </h2>
          <p className="sv-pricing-sub">
            Every project is different — these packages give you a clear starting
            point. Not sure which fits? Book a free 30-minute discovery call and
            we'll guide you.
          </p>

          <div className="sv-billing-toggle" role="tablist" aria-label="Engagement model">
            <button
              type="button" role="tab"
              aria-selected={billing === "fixed"}
              className={billing === "fixed" ? "is-active" : ""}
              onClick={() => setBilling("fixed")}
            >
              Fixed Scope
            </button>
            <button
              type="button" role="tab"
              aria-selected={billing === "retainer"}
              className={billing === "retainer" ? "is-active" : ""}
              onClick={() => setBilling("retainer")}
            >
              Retainer
              <span className="sv-save-pill">−15%</span>
            </button>
          </div>
        </header>

        {/* ── Main 3 premium tiers ── */}
        <div className="sv-tiers-trio">
          {mainTiers.map((t, i) => (
            <TierCard key={t.id} tier={t} index={i} />
          ))}
        </div>

        {/* ── More packages ── */}
        {morePackages.length > 0 && (
          <div className="sv-more-section">
            <div className="sv-more-header">
              <span className="sv-more-label">More Packages</span>
              <span className="sv-more-line"></span>
            </div>
            <div className="sv-compact-grid">
              {morePackages.map((t, i) => (
                <CompactCard key={t.id} tier={t} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── Add-ons ── */}
        {addons.length > 0 && (
          <div className="sv-more-section">
            <div className="sv-more-header">
              <span className="sv-more-label">Add-ons &amp; Extras</span>
              <span className="sv-more-line"></span>
            </div>
            <div className="sv-addon-grid">
              {addons.map((t, i) => (
                <AddonCard key={t.id} tier={t} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── Custom quote band ── */}
        <div className="sv-custom-quote">
          <div className="sv-custom-quote__icon">
            <i className="fa-solid fa-wand-magic-sparkles"></i>
          </div>
          <div className="sv-custom-quote__text">
            <span className="sv-custom-quote__eyebrow">Need something different?</span>
            <h3 className="sv-custom-quote__title">
              Custom engagements &amp; long-term partnerships
            </h3>
            <p className="sv-custom-quote__sub">
              Government platforms, AI research, dedicated squads for 6+ months,
              or multi-product builds — tell us the brief and we'll structure a
              tailored quote within 24 hours. All prices are negotiable based on
              scope, timeline, and budget.
            </p>
          </div>
          <NavLink to="/contact" className="sv-custom-quote__cta">
            <i className="fa-solid fa-paper-plane"></i>
            Request a Custom Quote
          </NavLink>
        </div>

        {/* ── Guarantees ── */}
        <div className="sv-guarantees">
          {GUARANTEES.map((g) => (
            <span key={g.id} className="sv-guarantee">
              <i className={g.icon}></i>
              {g.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;